/**
 * EmDispatchLogin — hospital-user sign-in for the Emergency Dispatch Console.
 *  Route: /emdispatch/login
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { emdApi, formatEmdError, EMD_TOKEN_KEY } from "../../lib/emdispatchApi";
import { SirenIcon, Hospital, ShieldCheck, Lock, EnvelopeSimple } from "@phosphor-icons/react";

export default function EmDispatchLogin() {
  const [mode, setMode] = useState("password"); // "password" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const requestOtp = async () => {
    if (!email) { toast.error("Enter your email first"); return; }
    setBusy(true);
    try {
      await emdApi.post("/public/emdispatch/login/otp", { email });
      setOtpSent(true);
      toast.success("Sign-in code sent — check your email");
    } catch (err) { toast.error(formatEmdError(err)); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      let r;
      if (mode === "password") {
        if (!password) { toast.error("Enter your password"); setBusy(false); return; }
        r = await emdApi.post("/public/emdispatch/login", { email, password });
      } else {
        if (!otpSent) { await requestOtp(); setBusy(false); return; }
        if (!otp) { toast.error("Enter the 6-digit code"); setBusy(false); return; }
        r = await emdApi.post("/public/emdispatch/login/verify", { email, otp });
      }
      localStorage.setItem(EMD_TOKEN_KEY, r.data.token);
      localStorage.setItem("rescigo_emdispatch_user", JSON.stringify(r.data.user));
      toast.success(`Signed in — ${r.data.user.hospital_name || "welcome"}`);
      nav("/emdispatch/console", { replace: true });
    } catch (err) {
      toast.error(formatEmdError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center relative overflow-hidden"
         style={{ background: "radial-gradient(circle at 20% 10%, #1E1B4B 0%, #0F172A 55%, #020617 100%)" }}
         data-testid="emd-login">
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 90% 30%, rgba(220,38,38,0.35) 0%, transparent 40%)" }}/>
      <div className="relative w-full max-w-md mx-4">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#DC2626] shadow-[0_10px_40px_rgba(220,38,38,0.55)] mb-3">
            <SirenIcon size={26} weight="fill" className="text-white"/>
          </div>
          <div className="font-heading text-white text-3xl leading-tight tracking-tight">
            EmDispatch
          </div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/60 mt-1">
            AI Emergency Dispatch Intelligence
          </div>
        </div>

        <form onSubmit={submit}
              className="bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-2xl border border-white/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4 flex items-center gap-1.5">
            <Hospital size={13} weight="duotone" className="text-[#DC2626]"/>
            Hospital dispatcher sign-in
          </div>

          {/* Password / OTP toggle */}
          <div className="flex bg-slate-100 rounded-full p-1 mb-4">
            {["password", "otp"].map(m => (
              <button key={m} type="button"
                       onClick={() => { setMode(m); setOtpSent(false); setOtp(""); }}
                       data-testid={`emd-login-mode-${m}`}
                       className={`flex-1 h-8 text-[11px] font-bold uppercase tracking-widest rounded-full transition-colors ${
                         mode === m ? "bg-white text-[#DC2626] shadow"
                                    : "text-slate-500"
                       }`}>
                {m === "password" ? "Password" : "Email OTP"}
              </button>
            ))}
          </div>

          <label className="block mb-3">
            <div className="text-[11px] font-semibold text-slate-500 mb-1">Email</div>
            <div className="relative">
              <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input type="email" required autoComplete="username"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      data-testid="emd-login-email"
                      placeholder="dispatcher@hospital.com"
                      className="w-full h-11 pl-9 pr-3 border border-slate-300 rounded-md text-sm focus:border-[#DC2626] outline-none"/>
            </div>
          </label>

          {mode === "password" ? (
            <label className="block mb-5">
              <div className="text-[11px] font-semibold text-slate-500 mb-1">Password</div>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="password" required autoComplete="current-password"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        data-testid="emd-login-password"
                        placeholder="••••••••"
                        className="w-full h-11 pl-9 pr-3 border border-slate-300 rounded-md text-sm focus:border-[#DC2626] outline-none"/>
              </div>
            </label>
          ) : (
            <div className="mb-5">
              {!otpSent ? (
                <button type="button" onClick={requestOtp} disabled={busy || !email}
                         data-testid="emd-login-send-otp"
                         className="w-full h-11 rounded-md border border-[#DC2626] text-[#DC2626] font-bold text-sm hover:bg-[#FEE2E2] disabled:opacity-50">
                  {busy ? "Sending…" : "Send code to my email"}
                </button>
              ) : (
                <label className="block">
                  <div className="text-[11px] font-semibold text-slate-500 mb-1">6-digit code from email</div>
                  <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          inputMode="numeric" maxLength={6}
                          data-testid="emd-login-otp"
                          placeholder="123456"
                          className="w-full h-11 px-3 border border-slate-300 rounded-md text-lg tracking-[0.4em] font-mono text-center focus:border-[#DC2626] outline-none"/>
                  <button type="button" onClick={requestOtp}
                           className="mt-2 text-[11px] text-[#DC2626] hover:underline">Resend code</button>
                </label>
              )}
            </div>
          )}

          <button type="submit" disabled={busy}
                   data-testid="emd-login-submit"
                   className="w-full h-11 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white font-bold text-sm inline-flex items-center justify-center gap-2 shadow-[0_6px_20px_rgba(220,38,38,0.4)]">
            {busy ? "Signing in…" : (<><ShieldCheck size={13} weight="bold"/> Sign in</>)}
          </button>

          <div className="mt-4 text-[11px] text-slate-500 leading-relaxed text-center">
            Credentials are issued by the super-admin. If you can&apos;t sign in,
            reach your hospital administrator.
          </div>
        </form>
      </div>
    </div>
  );
}
